import React, { memo, useEffect, useMemo, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { cloneDeep, isEmpty, merge, map, forEach } from 'lodash';
import {
  CartesianGrid,
  Legend,
  Area,
  AreaChart,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
  ReferenceDot,
} from 'recharts';
import { ResponsiveContainer } from 'recharts';

import { NoDataAvailable } from 'components/DataDisplay';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { useAppColorModeValue } from 'hooks/useAppColorMode';

import { DARK_COLORS, LIGHT_COLORS } from '../utils';

import { defaultStyles } from './styles';
import { IAreaChart } from './types';

export const CustomAreaChart: React.FC<IAreaChart> = ({
  data,
  xField,
  yField,
  legend = true,
  tooltip = true,
  styles,
  overwriteDefaultStyles = false,
  customTooltipContent,
  cartesianGrid = false,
  isLoading = false,
  referenceLines = [],
  referenceDots = [],
  colorMap,
  ignoreZero = false,
  tinyChart = false,
}) => {
  const COLORS = useAppColorModeValue(LIGHT_COLORS, DARK_COLORS);
  const baseStyles = useMemo(() => {
    const props = cloneDeep(defaultStyles);
    return overwriteDefaultStyles ? styles : merge(props, styles);
  }, [styles, overwriteDefaultStyles]);

  const [filterData, setFilterData] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    if (ignoreZero)
      setFilterData(
        map(data, o => {
          const obj: Record<string, any> = {};
          forEach(o, (i, key) => (obj[key] = i === 0 ? null : i));
          return obj;
        }),
      );
    else setFilterData(data);
  }, [data, ignoreZero]);

  return (
    <Box {...baseStyles?.root}>
      {isLoading ? (
        <CustomSpinner loading={isLoading} />
      ) : isEmpty(data) ? (
        <NoDataAvailable />
      ) : (
        <ResponsiveContainer>
          <AreaChart data={filterData} {...baseStyles?.areaChart}>
            <defs>
              {yField.map((each, index) => {
                const color =
                  colorMap?.[each] ||
                  baseStyles?.lineColors?.[index] ||
                  COLORS[index];
                return (
                  <linearGradient
                    id={'area-color-' + index}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={color} stopOpacity={0.2} />
                    <stop offset="8%" stopColor={color} stopOpacity={0.15} />
                    <stop offset="15%" stopColor={color} stopOpacity={0.07} />
                    <stop offset="20%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                );
              })}
            </defs>
            {!tinyChart && <XAxis dataKey={xField} {...baseStyles?.xAxis} />}
            {!tinyChart && (
              <YAxis {...baseStyles?.yAxis} allowDecimals={false} />
            )}

            {map(referenceLines, o => (
              <ReferenceLine {...o} />
            ))}
            {map(referenceDots, o => (
              <ReferenceDot {...o} />
            ))}

            {cartesianGrid && (
              <CartesianGrid {...(baseStyles?.cartesianGrid as any)} />
            )}
            {tooltip && (
              <Tooltip
                {...baseStyles?.tooltip}
                content={customTooltipContent}
              />
            )}
            {legend && <Legend {...(baseStyles?.legend as any)} />}
            {yField.map((each: string, index: number) => {
              const stroke =
                colorMap?.[each] ||
                baseStyles?.lineColors?.[index] ||
                COLORS[index];
              return (
                <Area
                  key={`line-${each}`}
                  dataKey={each}
                  stroke={stroke}
                  type="monotone"
                  fillOpacity={1}
                  fill={`url(#area-color-${index})`}
                  {...(baseStyles?.area as any)}
                />
              );
            })}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export const MemoAreaChart = memo<IAreaChart>(CustomAreaChart);
